import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Fab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { postsApi } from '../services/api';
import { normalizeImageUrl } from '../services/imageUtils';
import { Link, useNavigate } from 'react-router-dom';

const Posts = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [currentPost, setCurrentPost] = useState(null);
  const [formData, setFormData] = useState({
    text: '',
    gardenId: ''
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Visibility filter: all | visible | hidden
  const [visibilityFilter, setVisibilityFilter] = useState('all');

  // Search / filter inputs (draft — not applied until Search is clicked)
  const [filterGardenId, setFilterGardenId] = useState('');
  const [filterPostType, setFilterPostType] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterCountry, setFilterCountry] = useState('');

  // Applied filters (drive fetchPosts)
  const [appliedFilters, setAppliedFilters] = useState({
    gardenId: '', postType: '', city: '', country: '',
  });

  const isSearchActive = appliedFilters.gardenId || appliedFilters.postType ||
    appliedFilters.city || appliedFilters.country;

  const fetchPosts = async (pageNum = page, pageSize = rowsPerPage) => {
    try {
      setLoading(true);
      let data;
      if (isSearchActive) {
        data = await postsApi.adminSearchPosts({
          gardenId: appliedFilters.gardenId || undefined,
          postType: appliedFilters.postType || undefined,
          city: appliedFilters.city || undefined,
          country: appliedFilters.country || undefined,
          page: pageNum,
          size: pageSize,
        });
      } else {
        data = await postsApi.getAllPostsPaginated(pageNum, pageSize);
      }
      setPosts(data.content || []);
      setTotalElements(data.totalElements || 0);
      setTotalPages(data.totalPages || 0);
      setError('');
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Failed to load posts. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, appliedFilters]);

  const handleApplyFilters = () => {
    setAppliedFilters({
      gardenId: filterGardenId.trim(),
      postType: filterPostType,
      city: filterCity.trim(),
      country: filterCountry.trim(),
    });
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilterGardenId('');
    setFilterPostType('');
    setFilterCity('');
    setFilterCountry('');
    setAppliedFilters({ gardenId: '', postType: '', city: '', country: '' });
    setPage(0);
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = (post) => {
    setCurrentPost(post);
    setFormData({
      text: post.text,
      gardenId: post.garden?.id || ''
    });
    setOpenDialog(true);
  };

  const handleAddClick = () => {
    setCurrentPost(null);
    setFormData({
      text: '',
      gardenId: ''
    });
    setOpenDialog(true);
  };

  const handleDeleteClick = (post) => {
    setPostToDelete(post);
    setDeleteConfirmOpen(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentPost(null);
  };

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setPostToDelete(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSavePost = async () => {
    try {
      setLoading(true);

      // Validate form data
      if (!formData.text || !formData.gardenId) {
        setError('Text and Garden ID are required');
        setLoading(false);
        return;
      }

      // Create post data with media array
      const postData = {
        text: formData.text,
        gardenId: parseInt(formData.gardenId),
        media: [] // Empty media array as required by the API
      };

      if (currentPost) {
        // Update existing post
        const updatedPost = await postsApi.updatePost(currentPost.id, postData);

        // Refresh the posts list
        fetchPosts(page, rowsPerPage);

        setSuccessMessage(`Post has been updated.`);
      } else {
        // Create new post
        const newPost = await postsApi.createPost(postData);

        // Refresh the posts list
        fetchPosts(page, rowsPerPage);

        setSuccessMessage(`Post has been created.`);
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

    } catch (err) {
      console.error('Error saving post:', err);
      setError(`Failed to ${currentPost ? 'update' : 'create'} post: ${err.message}`);

      // Clear error message after 5 seconds
      setTimeout(() => {
        setError('');
      }, 5000);
    } finally {
      setLoading(false);
      handleCloseDialog();
    }
  };

  const handleDeletePost = async () => {
    try {
      setLoading(true);

      // Delete post from the backend
      await postsApi.deletePost(postToDelete.id);

      // Refresh the posts list
      fetchPosts(page, rowsPerPage);

      setSuccessMessage(`Post has been deleted.`);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

    } catch (err) {
      console.error('Error deleting post:', err);
      setError(`Failed to delete post: ${err.message}`);

      // Clear error message after 5 seconds
      setTimeout(() => {
        setError('');
      }, 5000);
    } finally {
      setLoading(false);
      handleCloseDeleteConfirm();
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')} sx={{ mb: 2 }}>
        Dashboard
      </Button>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Posts Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="visibility-filter-label">Visibility</InputLabel>
            <Select
              labelId="visibility-filter-label"
              id="visibility-filter"
              value={visibilityFilter}
              label="Visibility"
              onChange={(e) => setVisibilityFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="visible">Visible</MenuItem>
              <MenuItem value="hidden">Hidden</MenuItem>
            </Select>
          </FormControl>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={handleAddClick}
          >
            Create New Post
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Search / filter bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-end">
            <TextField
              label="Garden ID"
              placeholder="e.g. 42"
              value={filterGardenId}
              onChange={(e) => setFilterGardenId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
              size="small"
              type="number"
              sx={{ width: 140 }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Post Type</InputLabel>
              <Select
                value={filterPostType}
                label="Post Type"
                onChange={(e) => setFilterPostType(e.target.value)}
              >
                <MenuItem value="">All types</MenuItem>
                <MenuItem value="POST">Post</MenuItem>
                <MenuItem value="STORY">Story</MenuItem>
                <MenuItem value="REEL">Reel</MenuItem>
                <MenuItem value="LOST_DOG">Lost Dog</MenuItem>
                <MenuItem value="STRAY_DOG">Stray Dog</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Author city"
              placeholder="e.g. Tel Aviv"
              value={filterCity}
              onChange={(e) => setFilterCity(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
              size="small"
              sx={{ minWidth: 160 }}
            />
            <TextField
              label="Author country"
              placeholder="e.g. Israel"
              value={filterCountry}
              onChange={(e) => setFilterCountry(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
              size="small"
              sx={{ minWidth: 160 }}
            />
            <Button variant="contained" onClick={handleApplyFilters}>Search</Button>
            {isSearchActive && (
              <Button variant="outlined" onClick={handleClearFilters}>Clear</Button>
            )}
          </Stack>
          {isSearchActive && (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {appliedFilters.gardenId && <Chip size="small" label={`Garden: ${appliedFilters.gardenId}`} onDelete={() => { setFilterGardenId(''); setAppliedFilters(f => ({ ...f, gardenId: '' })); setPage(0); }} />}
              {appliedFilters.postType && <Chip size="small" label={`Type: ${appliedFilters.postType}`} onDelete={() => { setFilterPostType(''); setAppliedFilters(f => ({ ...f, postType: '' })); setPage(0); }} />}
              {appliedFilters.city && <Chip size="small" label={`City: ${appliedFilters.city}`} onDelete={() => { setFilterCity(''); setAppliedFilters(f => ({ ...f, city: '' })); setPage(0); }} />}
              {appliedFilters.country && <Chip size="small" label={`Country: ${appliedFilters.country}`} onDelete={() => { setFilterCountry(''); setAppliedFilters(f => ({ ...f, country: '' })); setPage(0); }} />}
            </Stack>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="posts table">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Text</TableCell>
                <TableCell>Media</TableCell>
                <TableCell>Author</TableCell>
                <TableCell>Post Type</TableCell>
                <TableCell>Audience</TableCell>
                <TableCell>Visible</TableCell>
                <TableCell align="right">Likes</TableCell>
                <TableCell align="right">Comments</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Updated Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(posts || []).filter(p => {
                if (visibilityFilter === 'visible') return p.visible === true || p.visible === null || p.visible === undefined;
                if (visibilityFilter === 'hidden') return p.visible === false;
                return true;
              }).map((post) => (
                <TableRow key={post.id}>
                  <TableCell>
                    <Link to={`/posts/${post.id}`} style={{ textDecoration: 'none' }}>
                      #{post.id}
                    </Link>
                  </TableCell>
                  <TableCell>{post.text}</TableCell>
                  <TableCell>
                    {post.media && post.media.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {post.media.map((media, index) => (
                          <Box 
                            key={index} 
                            component="img" 
                            src={normalizeImageUrl(media.smallUrl)}
                            alt={media.name}
                            sx={{ 
                              width: 50, 
                              height: 50, 
                              objectFit: 'cover',
                              borderRadius: 1
                            }}
                            title={media.name}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No media</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {post.user?.id ? (
                      <Link to={`/users/${post.user.id}`} style={{ textDecoration: 'none' }}>
                        {post.user.name || 'Unknown'}
                      </Link>
                    ) : (post.user?.name || 'Unknown')}
                  </TableCell>
                  <TableCell>{post.postType}</TableCell>
                  <TableCell>{post.audience}</TableCell>
                  <TableCell>
                    {post.visible === false ? (
                      <Chip label="Hidden" color="default" size="small" />
                    ) : (
                      <Chip label="Visible" color="success" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">{post.totalEmotions ?? 0}</TableCell>
                  <TableCell align="right">{post.totalComments ?? 0}</TableCell>
                  <TableCell>{new Date(post.createdTimestamp).toLocaleString()}</TableCell>
                  <TableCell>{new Date(post.updatedTimestamp).toLocaleString()}</TableCell>
                  <TableCell>
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={() => handleEditClick(post)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleDeleteClick(post)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 20, 50]}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Edit/Create Post Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{currentPost ? 'Edit Post' : 'Create New Post'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Text"
              name="text"
              value={formData.text}
              onChange={handleInputChange}
              multiline
              rows={4}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Garden ID"
              name="gardenId"
              value={formData.gardenId}
              onChange={handleInputChange}
              type="number"
              required
            />

            {/* Display media in edit dialog if available */}
            {currentPost && currentPost.media && currentPost.media.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1">Media:</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                  {currentPost.media.map((media, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center'
                      }}
                    >
                      <Box 
                        component="img" 
                        src={normalizeImageUrl(media.mediumUrl)}
                        alt={media.name}
                        sx={{ 
                          width: 100, 
                          height: 100, 
                          objectFit: 'cover',
                          borderRadius: 1
                        }}
                      />
                      <Typography variant="caption">{media.name}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSavePost} variant="contained" color="primary">
            {currentPost ? 'Update Post' : 'Create Post'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleCloseDeleteConfirm}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the post "{postToDelete?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirm}>Cancel</Button>
          <Button onClick={handleDeletePost} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Posts Management - Connected to backend API
        </Typography>
      </Box>

      {/* Floating action button for mobile */}
      <Box sx={{ display: { xs: 'block', sm: 'none' }, position: 'fixed', bottom: 16, right: 16 }}>
        <Fab color="primary" aria-label="add" onClick={handleAddClick}>
          <AddIcon />
        </Fab>
      </Box>
    </Container>
  );
};

export default Posts;
